import React from 'react';

/**
 * Ring-and-dot mark family, shared by the homepage service pillars and
 * /ai-adoption.
 *
 * Geometry is transcribed from the design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html, services section):
 * a 24-unit grid, 1.7 stroke, concentric rings resolving to a filled focal dot.
 *
 * One deliberate substitution from the reference: every stroke and fill is
 * `currentColor` rather than the literal #234B7A / #E3D8C3. That is what lets
 * the reversed ink-blue pillar recolour its mark by setting a text colour on
 * the wrapper, and it keeps the file free of hardcoded hex.
 *
 * The reference supplies three marks and the homepage needs four pillars, so
 * RingArcIcon is derived inside the same grammar rather than reusing a mark
 * twice. Same 24-unit grid, same 1.7 stroke, dot displaced off-centre to echo
 * the logo's focal placement.
 *
 * ArcRingIcon and TwinDotRingIcon (#7945) extend the family the same way:
 * /ai-adoption's problem section (4 items) and differentiators section
 * (6 items) need more variation than four marks provide. #7922 originally
 * forked a page-local copy of the first four marks into
 * `ai-adoption/components/icons.tsx` because this shared file didn't exist
 * yet at the time; #7945 reconciled that fork back into this one source
 * rather than carrying two near-identical icon sets. Add new marks here,
 * never fork a second set.
 */

interface ServiceIconProps {
  size?: number;
  className?: string;
}

const BASE_PROPS = {
  viewBox: '0 0 24 24',
  xmlns: 'http://www.w3.org/2000/svg',
  'aria-hidden': true,
  focusable: 'false',
} as const;

/** Filled ring and dot. Design reference, first services card. */
export function RingDotIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
    </svg>
  );
}

/** The full F-point lockup mark. Design reference, reversed services card. */
export function FPointMarkIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <rect x="3" y="2" width="1.7" height="20" fill="currentColor" />
      <rect x="3" y="2" width="13" height="1.7" fill="currentColor" />
      <rect x="3" y="10.2" width="9.4" height="1.7" fill="currentColor" />
      <circle cx="18" cy="18" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

/** Concentric triple ring. Design reference, third services card. */
export function TripleRingIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <circle
        cx="12"
        cy="12"
        r="9.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.35"
      />
      <circle cx="12" cy="12" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" />
    </svg>
  );
}

/** Derived fourth mark: the ring with its focal dot displaced off-centre. */
export function RingArcIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="16" cy="16" r="3.2" fill="currentColor" />
    </svg>
  );
}

/**
 * Fifth mark (#7945): an open arc rather than a closed ring, plus the
 * focal dot at its centre. Same grid, same stroke, a genuinely different
 * silhouette from RingDotIcon's closed circle.
 */
export function ArcRingIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <path
        d="M20.3 15.6A8.5 8.5 0 1 1 15.6 3.7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="2.6" fill="currentColor" />
    </svg>
  );
}

/**
 * Sixth mark (#7945): a closed ring holding two symmetric dots rather than
 * one centred one. No connecting line between the dots, so the grammar stays
 * ring-and-dot rather than drifting toward a node/edge diagram.
 */
export function TwinDotRingIcon({ size = 26, className }: ServiceIconProps) {
  return (
    <svg {...BASE_PROPS} width={size} height={size} className={className}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="8.4" cy="12" r="1.6" fill="currentColor" />
      <circle cx="15.6" cy="12" r="1.6" fill="currentColor" />
    </svg>
  );
}
